import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import FormField from "../../../components/ui/FormField";
import { Button } from "../../../components/Button";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";

import { directAndKmpApi } from "../../../api/DirectorAndKmp";

// ── Map frontend → backend request DTO ───────────────────────────────────────
const mapRegisterToRequest = (reg) => ({
  directorKmpPersonalDetails: {
    directorIdentificationNumber: reg.details.din || null,
    presentFullName:              reg.details.presentName || "",
    formerFullName:               reg.details.formerName || null,
    fathersName:                  reg.details.fatherName || null,
    mothersName:                  reg.details.motherName || null,
    spousesName:                  reg.details.spouseName || null,
    dateOfBirth:                  reg.details.dob || null,
    nationality:                  reg.details.nationality || "",
    occupation:                   reg.details.occupation || "",
    boardResolutionDate:          reg.details.resDate || null,
    appointmentDate:              reg.details.appointDate || null,
    cessationDate:                reg.details.cessationDate || null,
    cessationReason:              reg.details.cessationReason || null,
    otherDirectorshipsOrKmpPositions: reg.details.otherOffice || null,
    icsiMembershipNumber:         reg.details.icsiNo || null,
    permanentAccountNumber:       reg.details.pan || null,
  },

  residentialAddress: {
    present: {
      address:  reg.details.presentAddr || "",
      city:     reg.details.presentCity || "",
      district: reg.details.presentDistrict || "",
      state:    reg.details.presentState || "",
      pincode:  reg.details.presentPincode || "",
      country:  reg.details.presentCountry || "",
    },
    permanent: {
      address:  reg.details.permAddr || "",
      city:     reg.details.permCity || "",
      district: reg.details.permDistrict || "",
      state:    reg.details.permState || "",
      pincode:  reg.details.permPincode || "",
      country:  reg.details.permCountry || "",
    },
  },

  securityHoldingTransactions: (reg.securities || []).map((sec) => ({
    serialNumber:                          sec.sno ? parseInt(sec.sno, 10) : null,
    companyName:                           sec.compName || "",
    numberOfSecurities:                    sec.numSec ? parseInt(sec.numSec, 10) : null,
    descriptionOfSecurities:               sec.descSec || "",
    nominalValueOfSecurities:              sec.nomVal ? parseFloat(sec.nomVal) : null,
    dateOfAcquisition:                     sec.acqDate || null,
    pricePaidForAcquisition:               sec.acqPrice ? parseFloat(sec.acqPrice) : null,
    otherConsiderationPaidForAcquisition:  sec.acqOther || null,
    dateOfDisposal:                        sec.dispDate || null,
    priceReceivedOnDisposal:               sec.dispPrice ? parseFloat(sec.dispPrice) : null,
    otherConsiderationReceivedOnDisposal:  sec.dispOther || null,
    cumulativeBalanceAfterTransaction:     sec.cumBal ? parseFloat(sec.cumBal) : null,
    modeOfAcquisition:                     sec.modeAcq || "",
    modeOfHolding:                         sec.modeHold || "",
    pledgedOrEncumbered:                   sec.pledge === "true" || sec.pledge === true || false,
  })),
});

const BLANK_REGISTER = () => ({
  id: Date.now(),
  details: {},
  securities: [{ id: Date.now() }],
});

// ── Main Form ─────────────────────────────────────────────────────────────────
const RegisterDirAndKmpForm = () => {
  const token     = useSelector((state) => state.auth.access_token);
  const companies = useSelector((state) => state.account.accountDetails);

  const [accountId,    setAccountId]    = useState("");
  const [companyName,  setCompanyName]  = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const [registers, setRegisters] = useState(() => {
    const saved = localStorage.getItem("kmp_register_data");
    return saved
      ? JSON.parse(saved)
      : [
        {
          id: Date.now(),
          details: {},
          securities: [{ id: Date.now() }],
        },
      ];
  });

  useEffect(() => {
    localStorage.setItem("kmp_register_data", JSON.stringify(registers));
  }, [registers]);

  // ── Company selection ─────────────────────────────────────────────────────
  const handleCompanyChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === "") { setAccountId(""); setCompanyName(""); return; }
    const selected = companies.find((c) => String(c.id) === String(selectedValue));
    if (selected) { setAccountId(selected.id); setCompanyName(selected.accountName || ""); }
  };

  // ── Field updaters ────────────────────────────────────────────────────────
  const updateField = (id, section, field, value) => {
    setRegisters((prev) =>
      prev.map((reg) =>
        reg.id === id ? { ...reg, [section]: { ...reg[section], [field]: value } } : reg
      )
    );
  };

  const addSecurityEntry = (id) => {
    setRegisters((prev) =>
      prev.map((reg) =>
        reg.id === id
          ? { ...reg, securities: [...reg.securities, { id: Date.now() }] }
          : reg
      )
    );
  };

  const removeSecurityEntry = (regId, secId) => {
    setRegisters((prev) =>
      prev.map((reg) => {
        if (reg.id !== regId) return reg;
        if (reg.securities.length <= 1) return reg;
        return { ...reg, securities: reg.securities.filter((s) => s.id !== secId) };
      })
    );
  };

  const updateSecurityField = (regId, secId, field, value) => {
    setRegisters((prev) =>
      prev.map((reg) => {
        if (reg.id !== regId) return reg;
        return {
          ...reg,
          securities: reg.securities.map((sec) =>
            sec.id === secId ? { ...sec, [field]: value } : sec
          ),
        };
      })
    );
  };

  const addDirector = () => setRegisters([...registers, BLANK_REGISTER()]);

  const removeDirector = (id) => {
    if (registers.length > 1) setRegisters(registers.filter((r) => r.id !== id));
  };

  // ── Save / Submit ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!accountId) {
      setSubmitStatus({ type: "error", message: "Please select a company before saving." });
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      for (const reg of registers) {
        const payload = mapRegisterToRequest(reg);
        await directAndKmpApi.submitDirectorAndKmForm(payload, accountId, token);
      }
      setSubmitStatus({ type: "success", message: "Director / KMP Register data saved successfully!" });
      localStorage.removeItem("kmp_register_data");
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message: error.message || "Failed to save data. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="bg-white border border-gray-300 p-3 rounded shadow-sm mb-2 relative">
        <button
          onClick={() => window.history.back()}
          className="absolute top-3 left-4 flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Back</span>
        </button>
        <h1 className="text-center text-xl font-bold uppercase mb-1">
          Register of Directors and Key Managerial Personnel and their
          Shareholding
        </h1>
        <p className="text-center text-[10px] italic mb-2">
          [Pursuant to section 88 (1)(a) of the Companies Act, 2013 and rule
          3(1) of the Companies (Management and Administration) Rules, 2014]
        </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 border-b pb-3 mb-1">
          <FormField label="Name of the company" />
          <FormField label="Registered office address" type="description" />
          <div className="col-span-full font-bold text-xs italic text-gray-500">
            (TO BE MAINTAINED SEPARATELY FOR EACH DIRECTOR AND KMP)
          </div>
        </div>

      {/* ── Director / KMP Records ── */}
      <div className="space-y-10">
        {registers.map((reg, index) => (
          <div
            key={reg.id}
            className="border-2 border-gray-200 rounded-lg p-3 mb-2 relative bg-white"
          >
            <div className="absolute -top-3 left-4 bg-primary text-white px-3 py-1 text-sm font-semibold rounded">
              DIRECTOR / KMP RECORD #{index + 1}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              <div className="space-y-3">
                <FormField
                  label="Director Identification Number (optional for key managerial personnel)"
                  value={reg.details.din}
                  onChange={(v) => updateField(reg.id, "details", "din", v)}
                />
                <FormField
                  label="Present name and surname in full"
                  value={reg.details.presentName}
                  onChange={(v) =>
                    updateField(reg.id, "details", "presentName", v)
                  }
                />
                <FormField
                  label="Any former name or surname in full"
                  value={reg.details.formerName}
                  onChange={(v) =>
                    updateField(reg.id, "details", "formerName", v)
                  }
                />
                <FormField
                  label="Father's Name"
                  value={reg.details.fatherName}
                  onChange={(v) =>
                    updateField(reg.id, "details", "fatherName", v)
                  }
                />
                <FormField
                  label="Mother's Name"
                  value={reg.details.motherName}
                  onChange={(v) =>
                    updateField(reg.id, "details", "motherName", v)
                  }
                />
                <FormField
                  label="Spouse's Name (If married)"
                  value={reg.details.spouseName}
                  onChange={(v) =>
                    updateField(reg.id, "details", "spouseName", v)
                  }
                />
                <FormField
                  label="Date of birth"
                  type="date"
                  value={reg.details.dob}
                  onChange={(v) => updateField(reg.id, "details", "dob", v)}
                />
              </div>

              <div className="space-y-3">
                <FormField
                  label="Nationality (including the nationality of origin, if different)"
                  value={reg.details.nationality}
                  onChange={(v) =>
                    updateField(reg.id, "details", "nationality", v)
                  }
                />
                <FormField
                  label="Occupation"
                  value={reg.details.occupation}
                  onChange={(v) =>
                    updateField(reg.id, "details", "occupation", v)
                  }
                />
                <FormField
                  label="Date of the board resolution in which the appointment was made"
                  type="date"
                  value={reg.details.resDate}
                  onChange={(v) => updateField(reg.id, "details", "resDate", v)}
                />
                <FormField
                  label="Date of appointment and reappointment in the company"
                  type="date"
                  value={reg.details.appointDate}
                  onChange={(v) =>
                    updateField(reg.id, "details", "appointDate", v)
                  }
                />
                <FormField
                  label="Date of cessation of office and reasons therefor"
                  type="date"
                  value={reg.details.cessationDate}
                  onChange={(v) =>
                    updateField(reg.id, "details", "cessationDate", v)
                  }
                />
                <FormField
                  label="Office of director or KMP held or relinquished in any other body corporate"
                  type="description"
                  value={reg.details.otherOffice}
                  onChange={(v) =>
                    updateField(reg.id, "details", "otherOffice", v)
                  }
                />
                <FormField
                  label="Membership number of the ICSI in case of Company Secretary, if applicable"
                  value={reg.details.icsiNo}
                  onChange={(v) => updateField(reg.id, "details", "icsiNo", v)}
                />
                <FormField
                  label="Permanent Account Number (mandatory for KMP if not having DIN)"
                  type="pancard"
                  value={reg.details.pan}
                  onChange={(v) => updateField(reg.id, "details", "pan", v)}
                />
              </div>
            </div>

            <div className="mt-1 border-t pt-0">
              <h3 className="font-bold text-sm uppercase mb-1 underline">
                Residential address:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <FormField
                  label="Present Address"
                  type="description"
                  value={reg.details.presentAddr}
                  onChange={(v) =>
                    updateField(reg.id, "details", "presentAddr", v)
                  }
                />
                <FormField
                  label="Permanent Address"
                  type="description"
                  value={reg.details.permAddr}
                  onChange={(v) =>
                    updateField(reg.id, "details", "permAddr", v)
                  }
                />
              </div>
            </div>

            {/* Details of Securities Section (Replaces Table) */}
            <div className="mt-1 border-t pt-1">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-bold text-sm uppercase underline">
                  Details of securities held in the Company, its holding
                  company, subsidiaries...
                </h3>
                <button
                  onClick={() => addSecurityEntry(reg.id)}
                  className="bg-primary text-white px-3 py-1.5 text-sm rounded flex flex-row items-center"
                >
                  <Plus size={18}/> <span>Add Security Entry</span>
                </button>
            </div>

              {reg.securities.map((sec, sIdx) => (
                <div
                  key={sec.id}
                  className="p-2 bg-gray-50 border rounded-md mb-2"
                >
                  <div className="flex flex-row items-end justify-between">
                    <div className="col-span-full font-semibold text-sm uppercase text-gray-600">
                      Security Entry #{sIdx + 1}
                    </div>
                    {reg.securities.length > 1 &&
                    <button
                      type="button"
                      onClick={() => removeSecurityEntry(reg.id, sec.id)}
                      className="text-red-500"
                    >
                      <Trash2 size={18}/>
                    </button>}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2 p-2">
                    <FormField
                      label="(1) S. No."
                      value={sec.sno}
                      onChange={(v) =>
                        updateSecurityField(reg.id, sec.id, "sno", v)
                      }
                    />
                    <FormField
                      label="(2) Name of the Company"
                      value={sec.compName}
                      onChange={(v) =>
                        updateSecurityField(reg.id, sec.id, "compName", v)
                      }
                    />
                    <FormField
                      label="(4) Number of Securities"
                      type="number"
                      value={sec.numSec}
                      onChange={(v) =>
                        updateSecurityField(reg.id, sec.id, "numSec", v)
                      }
                    />
                    <FormField
                      label="(5) Description of Securities"
                      value={sec.descSec}
                      onChange={(v) =>
                        updateSecurityField(reg.id, sec.id, "descSec", v)
                      }
                    />
                    <FormField
                      label="(6) Nominal Value"
                      value={sec.nomVal}
                      onChange={(v) =>
                        updateSecurityField(reg.id, sec.id, "nomVal", v)
                      }
                    />
                    <FormField
                      label="(7) Date of Acquisition"
                      type="date"
                      value={sec.acqDate}
                      onChange={(v) =>
                        updateSecurityField(reg.id, sec.id, "acqDate", v)
                      }
                    />
                    <FormField
                      label="(8) Price paid for acquisition"
                      value={sec.acqPrice}
                      onChange={(v) =>
                        updateSecurityField(reg.id, sec.id, "acqPrice", v)
                      }
                    />
                    <FormField
                      label="(9) Other consideration for acquisition"
                      value={sec.acqOther}
                      onChange={(v) =>
                        updateSecurityField(reg.id, sec.id, "acqOther", v)
                      }
                    />
                    <FormField
                      label="(10) Date of disposal"
                      type="date"
                      value={sec.dispDate}
                      onChange={(v) =>
                        updateSecurityField(reg.id, sec.id, "dispDate", v)
                      }
                    />
                    <FormField
                      label="(11) Price received on disposal"
                      value={sec.dispPrice}
                      onChange={(v) =>
                        updateSecurityField(reg.id, sec.id, "dispPrice", v)
                      }
                    />
                    <FormField
                      label="(13) Cumulative balance held"
                      value={sec.cumBal}
                      onChange={(v) =>
                        updateSecurityField(reg.id, sec.id, "cumBal", v)
                      }
                    />
                    <FormField
                      label="(14) Mode of acquisition"
                      value={sec.modeAcq}
                      onChange={(v) =>
                        updateSecurityField(reg.id, sec.id, "modeAcq", v)
                      }
                    />
                    <FormField
                      label="(15) Mode: Physical / Demat"
                      value={sec.modeHold}
                      onChange={(v) =>
                        updateSecurityField(reg.id, sec.id, "modeHold", v)
                      }
                    />
                    <FormField
                      label="(16) Pledged/Encumbrance Details"
                      type="description"
                      value={sec.pledge}
                      onChange={(v) =>
                        updateSecurityField(reg.id, sec.id, "pledge", v)
                      }
                    />
                  </div>
                </div>
                  ))}
              </div>

            <button
              onClick={() =>
                setRegisters(registers.filter((r) => r.id !== reg.id))
              }
              className="mt-0 text-sm text-red-500 hover:underline flex flex-row items-center gap-2"
            >
              <span>Remove this Director Record</span> <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      {/* ── Action Buttons ── */}
      <div className="flex w-full justify-between items-center mt-6">
        <Button
          label="Add Another Director / KMP"
          onClick={addDirector}
          className="mt-2"
        />
        <Button
          label={isSubmitting ? "Saving..." : "Save Register Data"}
          onClick={handleSave}
          disabled={isSubmitting}
          className="mt-2"
        />
      </div>
    </div>
  );
};

export default RegisterDirAndKmpForm;